import type { ChatChannelType, ChatSupplierMscSettings } from '@frontend/api';

import type { ActionsOf } from 'store/types';

import { notify } from 'utils/reduxUtils';

import type { AnnotationContext } from '../annotationActions/annotationContext';
import type {
  AnnotationToolType,
  ChatDrawerStateByChannelType,
  ChatInitialFocus,
  ChatDrawerStateType,
  ChatId,
  CommentDraft,
  PartMasterVersion,
} from './types';

export const types = {
  ACKNOWLEDGE_CHANNEL_NOTIFICATIONS_REQUEST:
    'chat/ACKNOWLEDGE_CHANNEL_NOTIFICATIONS_REQUEST',
  ACKNOWLEDGE_THREAD_NOTIFICATIONS_REQUEST:
    'chat/ACKNOWLEDGE_THREAD_NOTIFICATIONS_REQUEST',
  ANNOTATION_INPUT_RECORD_EDITOR_STATE:
    'chat/ANNOTATION_INPUT_RECORD_EDITOR_STATE',
  ANNOTATION_TOOL_SET_ACTIVE: 'chat/ANNOTATION_TOOL_SET_ACTIVE',
  ANNOTATION_TOOL_SET_INACTIVE: 'chat/ANNOTATION_TOOL_SET_INACTIVE',
  CHANNELS_LIST_ERROR: 'chat/CHANNELS_LIST_ERROR',
  CHANNELS_LIST_REQUEST: 'chat/ANNOTATIONS_LIST_REQUEST',
  CHANNELS_LIST_SUCCESS: 'chat/ANNOTATIONS_LIST_SUCCESS',
  CHAT_APP_INIT: 'chat/CHAT_APP_INIT',
  CHAT_APP_INITIAL_CHANNEL_FOCUSED: 'chat/CHAT_APP_INITIAL_CHANNEL_FOCUSED',
  CHAT_APP_RESET: 'chat/CHAT_APP_RESET',
  CHAT_DRAWER_CLOSE: 'chat/CHAT_DRAWER_CLOSE',
  CHAT_DRAWER_OPEN: 'chat/CHAT_DRAWER_OPEN',
  CHAT_DRAWER_STATE_SET: 'chat/CHAT_DRAWER_STATE_SET',
  COMMENT_INPUT_RECORD_EDITOR_STATE: 'chat/COMMENT_INPUT_RECORD_EDITOR_STATE',
  COMMENTS_DISPLAY_ALL_COMMENTS: 'chat/COMMENTS_DISPLAY_ALL_COMMENTS',
  COMMENTS_TOGGLE_DISPLAY: 'chat/COMMENTS_TOGGLE_DISPLAY',
  CREATE_ANNOTATION_ERROR: 'chat/CREATE_ANNOTATION_ERROR',
  CREATE_ANNOTATION_INPUT_REINITIALIZED:
    'chat/CREATE_ANNOTATION_INPUT_REINITIALIZED',
  CREATE_ANNOTATION_OUTCOME_READ: 'chat/CREATE_ANNOTATION_OUTCOME_READ',
  CREATE_ANNOTATION_RECORD_ANNOATION_CREATING:
    'chat/CREATE_ANNOTATION_RECORD_ANNOATION_CREATING',
  CREATE_ANNOTATION_REQUEST: 'chat/CREATE_ANNOTATION_REQUEST',
  CREATE_ANNOTATION_SUCCESS: 'chat/CREATE_ANNOTATION_SUCCESS',
  CREATE_COMMENT_ERROR: 'chat/CREATE_COMMENT_ERROR',
  CREATE_COMMENT_INPUT_REINITIALIZED: 'chat/CREATE_COMMENT_INPUT_REINITIALIZED',
  CREATE_COMMENT_REQUEST: 'chat/CREATE_COMMENT_REQUEST',
  CREATE_COMMENT_SUCCESS: 'chat/CREATE_COMMENT_SUCCESS',
  DELETE_ANNOTATION_ERROR: 'chat/DELETE_ANNOTATION_ERROR',
  DELETE_ANNOTATION_REQUEST: 'chat/DELETE_ANNOTATION_REQUEST',
  DELETE_ANNOTATION_SUCCESS: 'chat/DELETE_ANNOTATION_SUCCESS',
  DELETE_COMMENT_ERROR: 'chat/DELETE_COMMENT_ERROR',
  DELETE_COMMENT_REQUEST: 'chat/DELETE_COMMENT_REQUEST',
  DELETE_COMMENT_SUCCESS: 'chat/DELETE_COMMENT_SUCCESS',
  EDIT_COMMENT_CANCEL_BY_ID: 'chat/EDIT_COMMENT_CANCEL_BY_ID',
  EDIT_COMMENT_ERROR: 'chat/EDIT_COMMENT_ERROR',
  EDIT_COMMENT_REQUEST: 'chat/EDIT_COMMENT_REQUEST',
  EDIT_COMMENT_SET_BY_ID: 'chat/EDIT_COMMENT_SET_BY_ID',
  EDIT_COMMENT_SUCCESS: 'chat/EDIT_COMMENT_SUCCESS',
  EDIT_MSC_CHANNEL_CONFIG_ERROR: 'chat/EDIT_MSC_CHANNEL_CONFIG_ERROR',
  EDIT_MSC_CHANNEL_CONFIG_REQUEST: 'chat/EDIT_MSC_CHANNEL_CONFIG_REQUEST',
  EDIT_MSC_CHANNEL_CONFIG_SUCCESS: 'chat/EDIT_MSC_CHANNEL_CONFIG_SUCCESS',
  FETCH_SUPPLIERS_MSC_SETTINGS_ERROR: 'chat/FETCH_SUPPLIERS_MSC_SETTINGS_ERROR',
  FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST:
    'chat/FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST',
  FETCH_SUPPLIERS_MSC_SETTINGS_SUCCESS:
    'chat/FETCH_SUPPLIERS_MSC_SETTINGS_SUCCESS',
  REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_ERROR:
    'chat/REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_ERROR',
  REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST:
    'chat/REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST',
  REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_SUCCESS:
    'chat/REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_SUCCESS',
  THREAD_SET_ACTIVE: 'chat/THREAD_SET_ACTIVE',
  THREAD_SET_INACTIVE: 'chat/THREAD_SET_INACTIVE',
} as const;

export type ChannelIdPayload = {
  channelId: ChatId;
};

export type AnnotationIdPayload = {
  annotationId: ChatId;
};

export type CommentIdPayload = {
  commentId: ChatId;
};

export type AnnotationInputRecordEditorStatePayload = {
  channelId: ChatId | null;
  channelType: ChatChannelType;
  editorState: CommentDraft;
};

export type AnnotationToolSetActivePayload = {
  channelId: ChatId | null;
  channelType: ChatChannelType;
  tool: AnnotationToolType;
};

export type ChatDrawerStateSetPayload = {
  activeChannelId: ChatId | null;
  activeChannelType: ChatChannelType | null;
  chatDrawerState: ChatDrawerStateType;
};

export type CommentInputRecordEditorStatePayload = {
  annotationId: ChatId;
  editorState: CommentDraft;
};

/** Names one start of chat, so the page that started it can follow it. */
export type ChatInitId = string;

export type ChatAppInitPayload = {
  /** Where a channel that chat opens on this page goes. */
  drawerStateByChannelType: ChatDrawerStateByChannelType;
  initId: ChatInitId;
  initialFocus: ChatInitialFocus;
  isExportControlled: boolean;
  isOwner: boolean;
  partMasterVersion: PartMasterVersion | null;
  techDataPackageRootUuid: string;
};

export type ChatInitialChannelFocusedPayload = {
  initId: ChatInitId;
  channelId: number;
  channelType: ChatChannelType;
};

export type CreateAnnotationErrorPayload = {
  channelId: ChatId | null;
  requestId: CreateAnnotationRequestId;
  error: unknown;
};

export type CreateAnnotationRecordCreatingPayload = {
  channelId: number;
  channelType: ChatChannelType;
  requestId: CreateAnnotationRequestId;
};

/**
 * Names one send, so whoever sent it can find out how it ended. Needed because
 * a send into a channel that does not exist yet has no channel id to match.
 */
export type CreateAnnotationRequestId = string;

export type CreateAnnotationRequestPayload = {
  /** `null` when the channel does not exist yet and should be created first */
  channelId: ChatId | null;
  channelType: ChatChannelType;
  comment: CommentDraft;
  actionUuid?: string | null;
  commentedOnQuoteItemId?: number | null;
  partMasterUuid?: string | null;
  partMasterVersion?: PartMasterVersion | null;
  requestId: CreateAnnotationRequestId;
  /** What the page adds to the annotation, collected before sending. */
  context?: AnnotationContext;
};

export type CreateAnnotationSuccessPayload = {
  channelId: ChatId | null;
  channelType: ChatChannelType;
  requestId: CreateAnnotationRequestId;
};

export type CreateCommentRequestPayload = {
  annotationId: ChatId;
  comment: CommentDraft;
};

export type EditCommentRequestPayload = {
  commentId: ChatId;
  content: CommentDraft;
};

export type EditMscChannelConfigRequestPayload = {
  mscChannelId: ChatId;
  mscSpecialistId: ChatId;
};

export type FetchSuppliersMscSettingsSuccessPayload = {
  suppliersMscSettings: ChatSupplierMscSettings;
};

export type ThreadSetActivePayload = {
  annotationId: ChatId;
  channelId: ChatId;
};

export const acknowledgeChannelNotifications = {
  request: ({ channelId }: ChannelIdPayload) => ({
    type: types.ACKNOWLEDGE_CHANNEL_NOTIFICATIONS_REQUEST,
    payload: { channelId },
  }),
};

export const acknowledgeThreadNotifications = {
  request: ({ annotationId }: AnnotationIdPayload) => ({
    type: types.ACKNOWLEDGE_THREAD_NOTIFICATIONS_REQUEST,
    payload: { annotationId },
  }),
};

export const annotationInput = {
  recordEditorState: ({
    channelId,
    channelType,
    editorState,
  }: AnnotationInputRecordEditorStatePayload) => ({
    type: types.ANNOTATION_INPUT_RECORD_EDITOR_STATE,
    payload: {
      channelId,
      channelType,
      editorState,
    },
  }),
};

export const annotationTool = {
  setActive: ({
    channelId,
    channelType,
    tool,
  }: AnnotationToolSetActivePayload) => ({
    type: types.ANNOTATION_TOOL_SET_ACTIVE,
    payload: { channelId, channelType, tool },
  }),
  setInactive: () => ({
    type: types.ANNOTATION_TOOL_SET_INACTIVE,
  }),
};

export const channelsList = {
  error: () => ({
    type: types.CHANNELS_LIST_ERROR,
  }),
  request: () => ({
    type: types.CHANNELS_LIST_REQUEST,
  }),
  success: () => ({
    type: types.CHANNELS_LIST_SUCCESS,
  }),
};

export const chatDrawer = {
  close: () => ({
    type: types.CHAT_DRAWER_CLOSE,
  }),
  open: () => ({
    type: types.CHAT_DRAWER_OPEN,
  }),
};

export const chatDrawerState = {
  set: ({
    activeChannelId,
    activeChannelType,
    chatDrawerState,
  }: ChatDrawerStateSetPayload) => ({
    type: types.CHAT_DRAWER_STATE_SET,
    payload: {
      activeChannelId,
      activeChannelType,
      chatDrawerState,
    },
  }),
};

export const commentInput = {
  recordEditorState: ({
    annotationId,
    editorState,
  }: CommentInputRecordEditorStatePayload) => ({
    type: types.COMMENT_INPUT_RECORD_EDITOR_STATE,
    payload: {
      annotationId,
      editorState,
    },
  }),
};

export const chatApp = {
  /** Starts chat for a part: loads its channels, then opens the initial focus. */
  init: ({
    drawerStateByChannelType,
    initId,
    initialFocus,
    isExportControlled,
    isOwner,
    partMasterVersion,
    techDataPackageRootUuid,
  }: ChatAppInitPayload) => ({
    type: types.CHAT_APP_INIT,
    payload: {
      drawerStateByChannelType,
      initId,
      initialFocus,
      isExportControlled,
      isOwner,
      partMasterVersion,
      techDataPackageRootUuid,
    },
  }),
  /** The initial focus found its channel, which chat is about to open. */
  initialChannelFocused: ({
    initId,
    channelId,
    channelType,
  }: ChatInitialChannelFocusedPayload) => ({
    type: types.CHAT_APP_INITIAL_CHANNEL_FOCUSED,
    payload: { initId, channelId, channelType },
  }),
  reset: () => ({
    type: types.CHAT_APP_RESET,
  }),
};

export const createAnnotation = {
  error: notify(
    ({ channelId, requestId, error }: CreateAnnotationErrorPayload) => ({
      type: types.CREATE_ANNOTATION_ERROR,
      payload: { channelId, requestId, error },
    }),
    'Failed to send message',
  ),
  inputReinitialized: (channelId: ChatId) => ({
    type: types.CREATE_ANNOTATION_INPUT_REINITIALIZED,
    payload: { channelId },
  }),
  /** The sender has seen how its request ended, so the record can go. */
  outcomeRead: (requestId: CreateAnnotationRequestId) => ({
    type: types.CREATE_ANNOTATION_OUTCOME_READ,
    payload: { requestId },
  }),
  /** A request has created the channel it was sent into. */
  recordAnnotationCreating: ({
    channelId,
    channelType,
    requestId,
  }: CreateAnnotationRecordCreatingPayload) => ({
    type: types.CREATE_ANNOTATION_RECORD_ANNOATION_CREATING,
    payload: {
      channelId,
      channelType,
      requestId,
    },
  }),
  request: ({
    channelId,
    channelType,
    comment,
    actionUuid,
    commentedOnQuoteItemId,
    partMasterUuid,
    partMasterVersion,
    requestId,
    context,
  }: CreateAnnotationRequestPayload) => ({
    type: types.CREATE_ANNOTATION_REQUEST,
    payload: {
      channelId,
      channelType,
      comment,
      actionUuid,
      commentedOnQuoteItemId,
      partMasterUuid,
      partMasterVersion,
      requestId,
      context,
    },
  }),
  success: ({
    channelId,
    channelType,
    requestId,
  }: CreateAnnotationSuccessPayload) => ({
    type: types.CREATE_ANNOTATION_SUCCESS,
    payload: {
      channelId,
      channelType,
      requestId,
    },
  }),
};

export const createComment = {
  error: notify(
    (error: unknown, annotationId?: ChatId | null) => ({
      type: types.CREATE_COMMENT_ERROR,
      payload: { error, annotationId },
    }),
    'Failed to save comment',
  ),
  inputReinitialized: (annotationId: ChatId) => ({
    type: types.CREATE_COMMENT_INPUT_REINITIALIZED,
    payload: { annotationId },
  }),
  request: ({ annotationId, comment }: CreateCommentRequestPayload) => ({
    type: types.CREATE_COMMENT_REQUEST,
    payload: { annotationId, comment },
  }),
  success: ({ annotationId }: AnnotationIdPayload) => ({
    type: types.CREATE_COMMENT_SUCCESS,
    payload: {
      annotationId,
    },
  }),
};

export const deleteAnnotation = {
  error: notify(
    (error: unknown) => ({
      type: types.DELETE_ANNOTATION_ERROR,
      payload: { error },
    }),
    'Failed to delete comment',
  ),
  request: ({ annotationId }: AnnotationIdPayload) => ({
    type: types.DELETE_ANNOTATION_REQUEST,
    payload: { annotationId },
  }),
  success: () => ({
    type: types.DELETE_ANNOTATION_SUCCESS,
  }),
};

export const deleteComment = {
  error: notify(
    (error: unknown) => ({
      type: types.DELETE_COMMENT_ERROR,
      payload: { error },
    }),
    'Failed to delete comment',
  ),
  request: ({ commentId }: CommentIdPayload) => ({
    type: types.DELETE_COMMENT_REQUEST,
    payload: { commentId },
  }),
  success: () => ({
    type: types.DELETE_COMMENT_SUCCESS,
  }),
};

export const editComment = {
  cancel: ({ commentId }: CommentIdPayload) => ({
    type: types.EDIT_COMMENT_CANCEL_BY_ID,
    payload: { commentId },
  }),
  // `notify` only forwards the first argument, so `commentId` is always
  // undefined when this is called through the wrapper.
  error: notify(
    (error: unknown, commentId?: ChatId | null) => ({
      type: types.EDIT_COMMENT_ERROR,
      payload: { error, commentId },
    }),
    'Failed to save comment',
  ),
  request: ({ commentId, content }: EditCommentRequestPayload) => ({
    type: types.EDIT_COMMENT_REQUEST,
    payload: { commentId, content },
  }),
  set: ({ commentId }: CommentIdPayload) => ({
    type: types.EDIT_COMMENT_SET_BY_ID,
    payload: { commentId },
  }),
  success: ({ commentId }: CommentIdPayload) => ({
    type: types.EDIT_COMMENT_SUCCESS,
    payload: { commentId },
  }),
};

export const editMscChannelConfig = {
  error: notify(
    (error: unknown) => ({
      type: types.EDIT_MSC_CHANNEL_CONFIG_ERROR,
      payload: { error },
    }),
    'Failed to update specialist',
  ),
  request: ({
    mscChannelId,
    mscSpecialistId,
  }: EditMscChannelConfigRequestPayload) => ({
    type: types.EDIT_MSC_CHANNEL_CONFIG_REQUEST,
    payload: { mscChannelId, mscSpecialistId },
  }),
  success: () => ({
    type: types.EDIT_MSC_CHANNEL_CONFIG_SUCCESS,
  }),
};

export const fetchSuppliersMscSettings = {
  error: () => ({
    type: types.FETCH_SUPPLIERS_MSC_SETTINGS_ERROR,
  }),
  request: () => ({
    type: types.FETCH_SUPPLIERS_MSC_SETTINGS_REQUEST,
  }),
  success: ({
    suppliersMscSettings,
  }: FetchSuppliersMscSettingsSuccessPayload) => ({
    type: types.FETCH_SUPPLIERS_MSC_SETTINGS_SUCCESS,
    payload: { suppliersMscSettings },
  }),
};

export const reinitializeTrustedThirdPartyChannel = {
  error: notify(
    (error: unknown) => ({
      type: types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_ERROR,
      payload: { error },
    }),
    'Failed to reinitialize the chat',
  ),
  request: () => ({
    type: types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_REQUEST,
  }),
  success: () => ({
    type: types.REINITIALIZE_TRUSTED_THIRD_PARTY_CHANNEL_SUCCESS,
  }),
};

export const threads = {
  setActive: ({ annotationId, channelId }: ThreadSetActivePayload) => ({
    type: types.THREAD_SET_ACTIVE,
    payload: { annotationId, channelId },
  }),
  setInactive: ({ channelId }: ChannelIdPayload) => ({
    type: types.THREAD_SET_INACTIVE,
    payload: { channelId },
  }),
};

export type ChatAction = ActionsOf<{
  acknowledgeChannelNotifications: typeof acknowledgeChannelNotifications;
  acknowledgeThreadNotifications: typeof acknowledgeThreadNotifications;
  annotationInput: typeof annotationInput;
  annotationTool: typeof annotationTool;
  channelsList: typeof channelsList;
  chatApp: typeof chatApp;
  chatDrawer: typeof chatDrawer;
  chatDrawerState: typeof chatDrawerState;
  commentInput: typeof commentInput;
  createAnnotation: typeof createAnnotation;
  createComment: typeof createComment;
  deleteAnnotation: typeof deleteAnnotation;
  deleteComment: typeof deleteComment;
  editComment: typeof editComment;
  editMscChannelConfig: typeof editMscChannelConfig;
  fetchSuppliersMscSettings: typeof fetchSuppliersMscSettings;
  reinitializeTrustedThirdPartyChannel: typeof reinitializeTrustedThirdPartyChannel;
  threads: typeof threads;
}>;
